import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { WsRequestPayload } from '@xdocs/ws-protocol';
import { useDebouncedValue } from '@xdocs/ui';
import type { CostEstimate, ErrorAlert } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

/** The four F2/F3/F4/F17 "killer feature" cost-preview ops — all share the
 * one `costEstimate` response shape (`./credit.ts`), same "estimate before
 * start" split as `generate.estimate`/`generate.start` (F48). */
export type ActionEstimateOp = 'expand.estimate' | 'cut.estimate' | 'difficulty.estimate' | 'translate.estimate';

export interface UseActionEstimateResult {
  estimate: CostEstimate | undefined;
  isLoading: boolean;
  isError: boolean;
  error: ErrorAlert | null;
}

/**
 * F48 live cost chip for the enhancement panels — mirrors
 * `routes/generate/useGenerateEstimate.ts`'s debounce/query pattern exactly.
 * `input === null` means "not enough to estimate yet" (e.g. F3's cut point
 * not chosen) — disables the query rather than firing an invalid request.
 * Per `document.dto.ts`'s own contract note: this NEVER blocks on tier —
 * a Free caller's estimate still resolves (the chip renders the PRO lock
 * via the panel's own upsell state, not by refusing to show a cost).
 */
export function useActionEstimate<Op extends ActionEstimateOp>(op: Op, input: WsRequestPayload<Op> | null): UseActionEstimateResult {
  const { wsClient, connectionState } = useWs();
  const debounced = useDebouncedValue(input, 400);
  const enabled = connectionState === 'connected' && debounced !== null;

  const query = useQuery<CostEstimate, ErrorAlert>({
    queryKey: [op, debounced],
    queryFn: () => requestTyped(wsClient, op, debounced as WsRequestPayload<Op>),
    enabled,
  });

  return {
    estimate: query.data,
    isLoading: query.isLoading || query.isFetching,
    isError: query.isError,
    error: query.isError ? query.error : null,
  };
}
